const AUTH_KEY = "tattvaa_admin_auth";

export const ROLE_CODES = {
  ADMIN: "admin",
  SUPPLIER: "supplier",
  SERVICE_PROVIDER: "service_provider",
  VERIFIER: "verifier",
  CUSTOMER: "customer",
  LOCATION_ADMIN_SUPPLY: "location_admin_supply",
  LOCATION_ADMIN_DEMAND: "location_admin_demand",
};

/** URL segment for each role (used in routes like /verifier/user-master) */
export const ROLE_ROUTE_SEGMENTS = {
  [ROLE_CODES.ADMIN]: "admin",
  [ROLE_CODES.SUPPLIER]: "supplier",
  [ROLE_CODES.SERVICE_PROVIDER]: "service-provider",
  [ROLE_CODES.VERIFIER]: "verifier",
};

/** All portal prefixes registered in the router */
export const PORTAL_ROUTE_SEGMENTS = Object.values(ROLE_ROUTE_SEGMENTS);

/** Non-admin roles that use the limited profile UI */
export const PROFILE_USER_ROLE_CODES = [
  ROLE_CODES.SUPPLIER,
  ROLE_CODES.SERVICE_PROVIDER,
];

/** Page keys suppliers & service providers can access */
export const PROVIDER_ALLOWED_PAGES = [
  "user-master",
  "address-master",
  "user-social-media",
  "user-awards-certificates",
  "user-news-media",
  "online-classes",
  "create-online-class",
];

/** Extra pages only the Supplier role can access (in addition to PROVIDER_ALLOWED_PAGES) */
export const SUPPLIER_ONLY_PAGES = ["asset-inventory"];

/** Full page allowlist for supplier logins */
export const SUPPLIER_ALLOWED_PAGES = [
  ...PROVIDER_ALLOWED_PAGES,
  ...SUPPLIER_ONLY_PAGES,
];

/** Page keys verifiers can access (logins list + same profile menus as other users) */
export const VERIFIER_ALLOWED_PAGES = [
  "user-details",
  "user-master",
  "address-master",
  "user-social-media",
  "user-awards-certificates",
  "user-news-media",
];

export const isAdminAuthenticated = () => {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw);
    return Boolean(data?.isAuthenticated);
  } catch {
    return false;
  }
};

export const getAdminUser = () => {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const getUserRoleCode = () => {
  const user = getAdminUser();
  return String(user?.user_role_code || "").toLowerCase();
};

export const isAdminRole = () => getUserRoleCode() === ROLE_CODES.ADMIN;

export const isSupplierRole = () => getUserRoleCode() === ROLE_CODES.SUPPLIER;

export const isVerifierRole = () => {
  if (getUserRoleCode() === ROLE_CODES.VERIFIER) return true;
  const roleName = String(getAdminUser()?.role || "").toLowerCase();
  return roleName === "verifier" || roleName === "verifyer";
};

export const isProviderRole = () => {
  const code = getUserRoleCode();
  return PROFILE_USER_ROLE_CODES.includes(code);
};

/** Route segment for the logged-in role, e.g. "verifier" | "admin" */
export const getRoleRouteSegment = () => {
  if (isAdminRole()) return ROLE_ROUTE_SEGMENTS[ROLE_CODES.ADMIN];
  if (isVerifierRole()) return ROLE_ROUTE_SEGMENTS[ROLE_CODES.VERIFIER];
  const code = getUserRoleCode();
  if (ROLE_ROUTE_SEGMENTS[code]) return ROLE_ROUTE_SEGMENTS[code];
  if (isProviderRole() && code) return code.replace(/_/g, "-");
  return ROLE_ROUTE_SEGMENTS[ROLE_CODES.ADMIN];
};

/** Base path for the logged-in role, e.g. "/verifier" */
export const getRoleRouteBase = () => `/${getRoleRouteSegment()}`;

/** Build a full app path under the current role, e.g. buildAppPath("user-master") → "/verifier/user-master" */
export const buildAppPath = (page = "") => {
  const clean = String(page || "").replace(/^\/+/, "");
  const base = getRoleRouteBase();
  return clean ? `${base}/${clean}` : base;
};

export const getPathPortal = (pathname) => {
  const parts = String(pathname || "")
    .split("/")
    .filter(Boolean);
  return parts[0] || "";
};

export const getPathPage = (pathname) => {
  const parts = String(pathname || "")
    .split("/")
    .filter(Boolean);
  return parts.slice(1).join("/") || "";
};

/** Linked user_master.user_id for supplier / service-provider logins */
export const getReferenceUserId = () => {
  const user = getAdminUser();
  const id = user?.reference_id;
  if (id == null || id === "") return null;
  const numeric = Number(id);
  return Number.isNaN(numeric) ? null : numeric;
};

/** Non-admins only see/edit their own user-scoped master data */
export const shouldScopeToOwnUserData = () =>
  isAdminAuthenticated() &&
  !isAdminRole() &&
  (isProviderRole() || isVerifierRole());

const pageAllowed = (page, allowedPages) =>
  allowedPages.some(
    (allowed) => page === allowed || page.startsWith(`${allowed}/`)
  );

export const canAccessAdminPath = (pathname) => {
  if (!isAdminAuthenticated()) return false;

  const portal = getPathPortal(pathname);
  const page = getPathPage(pathname);

  if (portal !== getRoleRouteSegment()) return false;
  if (isAdminRole()) return true;
  if (isVerifierRole()) return pageAllowed(page, VERIFIER_ALLOWED_PAGES);
  if (isSupplierRole()) return pageAllowed(page, SUPPLIER_ALLOWED_PAGES);
  if (!isProviderRole()) return false;
  return pageAllowed(page, PROVIDER_ALLOWED_PAGES);
};

export const getDefaultAdminRoute = () => {
  if (isAdminRole()) return buildAppPath("dashboard");
  if (isVerifierRole()) return buildAppPath("user-details");
  if (isProviderRole()) return buildAppPath("user-master");
  return "/login";
};

export const notifyAuthChanged = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("auth-changed"));
  }
};

/** user_master.user_id used by wishlist / cart APIs */
export const getAuthUserId = () => {
  if (!isAdminAuthenticated()) return null;
  return getReferenceUserId();
};

/** True when storefront can use wishlist/cart (valid linked user_master id). */
export const hasShopUserId = () => {
  const id = getAuthUserId();
  return id != null && Number(id) > 0;
};

export const getShopAuthLabel = () => {
  const user = getAdminUser();
  if (!user?.isAuthenticated) return "";
  return String(user.name || user.phone || "Account").trim();
};

export const loginUser = (user) => {
  const payload = {
    isAuthenticated: true,
    login_id: user.login_id,
    phone: user.phone,
    name: user.name || "User",
    role: user.user_role || "User",
    user_role_code: user.user_role_code,
    user_role_id: user.user_role_id,
    reference_id: user.reference_id,
    token: user.token || null,
    loggedInAt: new Date().toISOString(),
  };
  localStorage.setItem(AUTH_KEY, JSON.stringify(payload));
  notifyAuthChanged();
  return payload;
};

/** @deprecated Use loginUser */
export const loginAdmin = (phone) =>
  loginUser({
    phone,
    name: "Admin",
    user_role: "System Admin",
    user_role_code: ROLE_CODES.ADMIN,
  });

export const logoutAdmin = () => {
  localStorage.removeItem(AUTH_KEY);
  notifyAuthChanged();
};
